import React, { useEffect, useState, useMemo } from 'react';
import { ShoppingCart, Search, ChevronDown, ChevronRight, Package, User, RefreshCw, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

interface CartItem {
  sku: string;
  title: string;
  brand: string;
  category: string;
  qty: number;
  listino10?: number;
  priceOverride?: number;
  stock?: number;
}

interface CartEntry {
  userId: string;
  an_conto: number | null;
  clientName: string;
  userEmail: string | null;
  ruolo: string | null;
  itemCount: number;
  totalQty: number;
  totalValue: number;
  updated_at: string | null;
  items: CartItem[];
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtEur(val: number): string {
  return '€\u00a0' + val.toFixed(2).replace('.', ',');
}

export default function AdminCarrelliPage() {
  const [carts, setCarts] = useState<CartEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [lastFetch, setLastFetch] = useState<Date | null>(null);

  const fetchCarts = () => {
    setLoading(true);
    api.get<{ carts: CartEntry[]; total: number }>('/api/admin/carts')
      .then(r => { setCarts(r.data.carts); setLastFetch(new Date()); })
      .catch(() => setCarts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCarts(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return carts;
    return carts.filter(c =>
      c.clientName.toLowerCase().includes(q) ||
      (c.userEmail || '').toLowerCase().includes(q) ||
      String(c.an_conto || '').includes(q) ||
      c.items.some(i => i.sku.toLowerCase().includes(q) || i.title.toLowerCase().includes(q))
    );
  }, [carts, search]);

  const totalItems = carts.reduce((s, c) => s + c.totalQty, 0);
  const totalValue = carts.reduce((s, c) => s + c.totalValue, 0);

  const toggleExpand = (userId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-carrelli-page">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-red-600" />
            Carrelli Utenti
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {carts.length} carrelli attivi · {totalItems} articoli totali · valore complessivo {fmtEur(totalValue)}
          </p>
        </div>
        <button
          onClick={fetchCarts}
          disabled={loading}
          data-testid="carrelli-refresh-btn"
          className="flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-medium hover:bg-secondary/60 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: 'Carrelli attivi', value: carts.length, color: 'text-[#2B5EA7]' },
          { label: 'Articoli totali', value: totalItems, color: 'text-emerald-600' },
          { label: 'Valore totale', value: fmtEur(totalValue), color: 'text-amber-600' },
        ].map(k => (
          <div key={k.label} className="rounded-xl border bg-white p-4">
            <div className={`text-xl font-bold tabular-nums ${k.color}`}>{k.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Ricerca */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Cerca per cliente, email, an_conto, SKU…"
          className="w-full h-9 pl-9 pr-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
          data-testid="carrelli-search"
        />
      </div>

      {/* Aggiornato */}
      {lastFetch && (
        <p className="text-[11px] text-muted-foreground mb-3">
          Aggiornato alle {lastFetch.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </p>
      )}

      {/* Lista carrelli */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground gap-2 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" /> Caricamento carrelli…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          {search ? 'Nessun carrello corrisponde alla ricerca.' : 'Nessun carrello attivo.'}
        </div>
      ) : (
        <div className="space-y-2" data-testid="carrelli-list">
          {filtered.map(cart => {
            const isOpen = expanded.has(cart.userId);
            return (
              <div key={cart.userId} className="rounded-xl border bg-white overflow-hidden" data-testid={`cart-row-${cart.userId}`}>

                {/* Riga principale */}
                <button
                  onClick={() => toggleExpand(cart.userId)}
                  className="w-full flex items-center gap-4 px-4 py-3.5 hover:bg-[hsl(var(--dtk-canvas))] transition-colors text-left"
                  data-testid={`cart-expand-${cart.userId}`}
                >
                  {/* Espandi */}
                  <span className="text-muted-foreground flex-shrink-0">
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </span>

                  {/* Cliente */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                      {cart.an_conto ? (
                        <Link
                          to={`/admin/clienti/${cart.an_conto}`}
                          onClick={e => e.stopPropagation()}
                          className="text-sm font-semibold truncate hover:text-[#2B5EA7] hover:underline"
                          data-testid={`cart-client-name-link-${cart.userId}`}
                        >
                          {cart.clientName || cart.userEmail || cart.userId.slice(0, 8) + '…'}
                        </Link>
                      ) : (
                        <span className="text-sm font-semibold truncate">
                          {cart.clientName || cart.userEmail || cart.userId.slice(0, 8) + '…'}
                        </span>
                      )}
                      {cart.an_conto && (
                        <Link
                          to={`/admin/clienti/${cart.an_conto}`}
                          onClick={e => e.stopPropagation()}
                          className="text-[10px] text-[#2B5EA7] hover:underline flex items-center gap-0.5"
                          data-testid={`cart-client-link-${cart.userId}`}
                        >
                          {cart.an_conto} <ExternalLink className="w-2.5 h-2.5" />
                        </Link>
                      )}
                    </div>
                    {cart.userEmail && (
                      <div className="text-[11px] text-muted-foreground mt-0.5 truncate pl-5">{cart.userEmail}</div>
                    )}
                  </div>

                  {/* Articoli */}
                  <div className="text-center flex-shrink-0 w-20">
                    <div className="flex items-center justify-center gap-1">
                      <Package className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm font-semibold tabular-nums">{cart.itemCount}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground">linee</div>
                  </div>

                  {/* Qty */}
                  <div className="text-center flex-shrink-0 w-16">
                    <div className="text-sm font-semibold tabular-nums">{cart.totalQty}</div>
                    <div className="text-[10px] text-muted-foreground">pezzi</div>
                  </div>

                  {/* Valore */}
                  <div className="text-right flex-shrink-0 w-28">
                    <div className="text-sm font-bold text-[#2B5EA7] tabular-nums">{fmtEur(cart.totalValue)}</div>
                    <div className="text-[10px] text-muted-foreground">valore stimato</div>
                  </div>

                  {/* Data */}
                  <div className="text-right flex-shrink-0 w-32 hidden sm:block">
                    <div className="text-[11px] text-muted-foreground">{fmtDate(cart.updated_at)}</div>
                    <div className="text-[10px] text-muted-foreground">Data ultima modifica</div>
                  </div>
                </button>

                {/* Dettaglio articoli */}
                {isOpen && (
                  <div className="border-t bg-[hsl(var(--dtk-canvas))] px-4 py-3">
                    <table className="w-full text-xs" data-testid={`cart-items-${cart.userId}`}>
                      <thead>
                        <tr className="text-muted-foreground border-b">
                          <th className="text-left pb-2 font-medium">SKU</th>
                          <th className="text-left pb-2 font-medium">Descrizione</th>
                          <th className="text-left pb-2 font-medium">Marca</th>
                          <th className="text-center pb-2 font-medium">Qty</th>
                          <th className="text-right pb-2 font-medium">Prezzo unit.</th>
                          <th className="text-right pb-2 font-medium">Totale riga</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {cart.items.map(item => {
                          const unitPrice = item.priceOverride ?? item.listino10 ?? 0;
                          const rowTotal = unitPrice * item.qty;
                          return (
                            <tr key={item.sku} className="hover:bg-white/60 transition-colors">
                              <td className="py-2 pr-3 font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                                <Link to={`/admin/prodotti/${item.sku}`} className="hover:text-[#2B5EA7] hover:underline">
                                  {item.sku}
                                </Link>
                              </td>
                              <td className="py-2 pr-3 max-w-[280px] truncate" title={item.title}>{item.title}</td>
                              <td className="py-2 pr-3 text-muted-foreground">{item.brand}</td>
                              <td className="py-2 text-center font-semibold tabular-nums">{item.qty}</td>
                              <td className="py-2 text-right tabular-nums text-muted-foreground">
                                {unitPrice > 0 ? fmtEur(unitPrice) : '—'}
                              </td>
                              <td className="py-2 text-right font-semibold tabular-nums text-[#2B5EA7]">
                                {rowTotal > 0 ? fmtEur(rowTotal) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
